'use client';

import { Box, Flex, IconButton, Popover, Portal, Text } from '@chakra-ui/react';
import { memo, useCallback, useEffect } from 'react';
import { LuList, LuPlus, LuSettings } from 'react-icons/lu';

import useControlledPopover from 'hooks/useControlledPopover';

import NodePickerPopover from './NodePickerPopover';
import { type CatalogItem, PickerMode } from './types';

interface Props {
  onAddNode: (item: CatalogItem) => void;
  onCloseCanvasPopovers: () => void;
  initialView?: 'picker' | 'list' | null;
}

const WorkflowSidebar = ({ onAddNode, onCloseCanvasPopovers, initialView }: Props) => {
  const picker = useControlledPopover<HTMLDivElement, HTMLButtonElement>();
  const workflowsList = useControlledPopover<HTMLDivElement, HTMLButtonElement>();

  useEffect(() => {
    if (initialView === 'picker') {
      picker.onOpen();
    } else if (initialView === 'list') {
      workflowsList.onOpen();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePickerOpen = useCallback(() => {
    workflowsList.onClose();
    onCloseCanvasPopovers();
    picker.onOpen();
  }, [picker, workflowsList, onCloseCanvasPopovers]);

  const handleWorkflowsOpen = useCallback(() => {
    picker.onClose();
    onCloseCanvasPopovers();
    workflowsList.onOpen();
  }, [picker, workflowsList, onCloseCanvasPopovers]);

  const handlePickerSelect = useCallback(
    (item: CatalogItem) => {
      onAddNode(item);
      picker.onClose();
    },
    [onAddNode, picker],
  );

  return (
    <Flex
      direction="column"
      w="48px"
      bg="surface.muted"
      borderRightWidth="1px"
      borderRightColor="border.light"
      alignItems="center"
      py={3}
      gap={2}
      flexShrink={0}
    >
      {/* Add Node */}
      <Popover.Root
        open={picker.open}
        onOpenChange={(d) => (d.open ? handlePickerOpen() : picker.onClose())}
        positioning={{ placement: 'right-start' }}
      >
        <Popover.Trigger asChild>
          <IconButton
            ref={picker.triggerRef}
            size="sm"
            variant="ghost"
            borderRadius="md"
            color="text.secondary"
            _hover={{ color: 'text.primary', bg: 'surface.hover' }}
            aria-label="Add node"
          >
            <LuPlus size={18} />
          </IconButton>
        </Popover.Trigger>
        <Portal>
          <Popover.Positioner>
            <Popover.Content
              ref={picker.contentRef}
              p={0}
              border="none"
              bg="transparent"
              boxShadow="none"
            >
              <NodePickerPopover
                mode={PickerMode.Add}
                onSelect={handlePickerSelect}
                onClose={picker.onClose}
              />
            </Popover.Content>
          </Popover.Positioner>
        </Portal>
      </Popover.Root>
      {/* Workflows List */}
      <Popover.Root
        open={workflowsList.open}
        onOpenChange={(d) => (d.open ? handleWorkflowsOpen() : workflowsList.onClose())}
        positioning={{ placement: 'right-start' }}
      >
        <Popover.Trigger asChild>
          <IconButton
            ref={workflowsList.triggerRef}
            size="sm"
            variant="ghost"
            borderRadius="md"
            color="text.secondary"
            _hover={{ color: 'text.primary', bg: 'surface.hover' }}
            aria-label="View workflows"
          >
            <LuList size={18} />
          </IconButton>
        </Popover.Trigger>
        <Portal>
          <Popover.Positioner>
            <Popover.Content
              ref={workflowsList.contentRef}
              borderRadius="xl"
              boxShadow="lg"
              bg="surface.background"
              borderWidth="1px"
              borderColor="border.light"
              w="260px"
              p={0}
            >
              <Flex direction="column" p={3} gap={2}>
                <Text fontSize="sm" fontWeight="semibold">
                  Workflows
                </Text>
                <Flex
                  direction="column"
                  gap={1}
                >
                  <Text fontSize="xs" color="text.placeholder" py={4} textAlign="center">
                    No saved workflows yet
                  </Text>
                </Flex>
              </Flex>
            </Popover.Content>
          </Popover.Positioner>
        </Portal>
      </Popover.Root>
      {/* Settings placeholder */}
      <Box mt="auto">
        <IconButton
          size="sm"
          variant="ghost"
          borderRadius="md"
          color="text.secondary"
          _hover={{ color: 'text.primary', bg: 'surface.hover' }}
          aria-label="Workflow settings"
        >
          <LuSettings size={18} />
        </IconButton>
      </Box>
    </Flex>
  );
};

export default memo(WorkflowSidebar);
